/**
 * Varios alumnos a la vez: lo que no es pantalla · 03-11-2026
 *
 * Lo pidió el primer club: «coges todos los niños de este año, el otro y el
 * siguiente y del tirón rellenas ciertos datos y los metes en el grupo». Uno por uno
 * sigue igual; esto es para lo común a muchos (`endpoints.md` §P.1, «Varios alumnos
 * a la vez»).
 *
 * La selección vive en un `Map` de id a nombre: el nombre es lo que se enseña en la
 * barra y en el resumen, y los marcados de la página 1 siguen marcados al pasar a la
 * 2, aunque ya no estén en pantalla.
 */

export type Seleccion = ReadonlyMap<string, string>;

export const NINGUNO: Seleccion = new Map();

/** Marca o desmarca uno. */
export function alternar(
  sel: Seleccion,
  id: string,
  nombre: string
): Seleccion {
  const nueva = new Map(sel);
  if (nueva.has(id)) nueva.delete(id);
  else nueva.set(id, nombre);
  return nueva;
}

/** Los de esta página: si están todos, los quita; si no, los pone. */
export function alternarPagina(
  sel: Seleccion,
  pagina: readonly { readonly id: string; readonly nombre: string }[]
): Seleccion {
  const nueva = new Map(sel);
  if (
    todosMarcados(
      sel,
      pagina.map((a) => a.id)
    )
  ) {
    for (const a of pagina) nueva.delete(a.id);
  } else {
    for (const a of pagina) nueva.set(a.id, a.nombre);
  }
  return nueva;
}

export const todosMarcados = (sel: Seleccion, ids: readonly string[]) =>
  ids.length > 0 && ids.every((id) => sel.has(id));

/** Añade muchos de golpe: «todos los que cumplen el filtro». */
export function conTodos(
  sel: Seleccion,
  alumnos: readonly { readonly id: string; readonly nombre: string }[]
): Seleccion {
  const nueva = new Map(sel);
  for (const a of alumnos) nueva.set(a.id, a.nombre);
  return nueva;
}

/**
 * Tras aplicar, **se quedan marcados los que se saltaron**: son con los que hay
 * que hacer algo, y así se puede volver a intentar sin buscarlos.
 */
export function soloLosSaltados(
  sel: Seleccion,
  saltados: readonly { readonly student_id: string }[]
): Seleccion {
  const quedan = new Set(saltados.map((s) => s.student_id));
  return new Map([...sel].filter(([id]) => quedan.has(id)));
}

export const alumnosEnTexto = (n: number) =>
  n === 1 ? '1 alumno' : `${n} alumnos`;

export type EstadoEnBloque = 'active' | 'trial' | 'paused' | 'left';

/**
 * El cuerpo de «Cambiar el estado». Una baja lleva su fecha y su motivo; cualquier
 * otro estado **borra** la baja que hubiera, que es lo que significa volver.
 */
export function cambiosDeEstado(
  estado: EstadoEnBloque,
  fecha: string,
  motivo: string
): {
  status: EstadoEnBloque;
  left_on: string | null;
  leave_reason: string | null;
} {
  return estado === 'left'
    ? {
        status: 'left',
        left_on: fecha,
        leave_reason: motivo.trim() === '' ? null : motivo.trim(),
      }
    : { status: estado, left_on: null, leave_reason: null };
}

/** Hoy en `YYYY-MM-DD` **local**: lo que espera un `<input type="date">`. */
export const hoyISO = (ahora = new Date()): string =>
  `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(
    ahora.getDate()
  ).padStart(2, '0')}`;
